import React, { FC, useEffect, useMemo, useState } from 'react'
import { Container, Row, Col, Form, Button, Spinner } from 'react-bootstrap'
import PageTitle from '../../components/PageTitle'
import { List } from '@teschglobal/tg-fhir-react-components/dist/types'
import useFullProviderFhirResource from '../../hooks/admin/useFullProviderFhirResource'
import DrugResults from './DrugResults'
import { alphabet } from '../../utils/constants'
import { useLocation } from 'react-router-dom'
import { Typeahead } from 'react-bootstrap-typeahead'

const DrugLookup: FC = () => {
    const [selectedPlan, setSelectedPlan] = useState('')
    const [selectedLetter, setSelectedLetter] = useState('')
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedDrug, setSelectedDrug] = useState('')
    const [error, setError] = useState('')
    const location = useLocation()
    const isTextSearch = searchTerm.length >= 3


    const planId = selectedPlan
        ? selectedPlan.split('-').slice(1).join('-')
        : ''

    const {
        refetch: getLists,
        data: listData,
        isFetching: isFetchingList,
    } = useFullProviderFhirResource({
        resource: 'List',
        searchTerms: '',
        pageSize: 500
    })

    const {
        refetch: getDrugLookup,
        data: drugLookupData,
        isFetching: isFetchingDrugLookup,
    } = useFullProviderFhirResource({
        resource: 'MedicationKnowledge',
        searchTerms: {
            'drug-plan': planId,
            ...(selectedDrug
                ? { 'code:text': selectedDrug }
                : {})
        },
        drugLetter: selectedDrug ? undefined : selectedLetter,
        pageSize: 500
    })

    const {
        refetch: getSuggestions,
        data: suggestionData,
        isFetching: isFetchingSuggestions,
    } = useFullProviderFhirResource({
        resource: 'MedicationKnowledge',
        searchTerms: {
            'drug-plan': planId,
            ...(isTextSearch
                ? { 'code:text': searchTerm }
                : {})
        },
        pageSize: 10
    })

    useEffect(() => {
        if (!listData) getLists()
    }, [])

    useEffect(() => {
        if (!selectedPlan) return
        if (!selectedLetter && !selectedDrug) return

        getDrugLookup()
    }, [selectedPlan, selectedLetter, selectedDrug])

  useEffect(() => {
    if (!selectedPlan) return
    if (!isTextSearch) return

    getSuggestions()
}, [searchTerm, selectedPlan])

    useEffect(() => {
        setSelectedPlan('')
        setSelectedLetter('')
        setSearchTerm('')
        setSelectedDrug('')
        setError('')
    }, [location.key])

    const planOptions: List[] = useMemo(() => {
        if (!listData?.data) return []
        return listData.data.map((list: List) => ({
            id: list.id,
            title: list.title || list.identifier?.[0]?.value
        }))
    }, [listData])

    const handleDrugLookup = () => {
        if (!selectedPlan) {
            setError('Please choose a plan.')
            return
        }
        setSelectedLetter('A')
        setSelectedDrug('')
        setSearchTerm('')
    }

    return (
        <section className="content">
            <Container>
                <PageTitle displayName="Drug Lookup" />
                <h1 className="headerText h2">Drug Lookup</h1>
                <p>Find drugs and coverage for your plan</p>

                {isFetchingList ? (
                    <Spinner animation="border" />
                ) : (
                    <Row>
                        <Col md={6}>
                            <Form.Select
                                value={selectedPlan}
                                onChange={(e) => {
                                    setSelectedPlan(e.target.value)
                                    setSelectedLetter('')
                                    setSelectedDrug('')
                                    setSearchTerm('')
                                    setError('')
                                }}
                                isInvalid={!!error}
                            >
                                <option value="">Choose Plan</option>
                                {planOptions.map((plan, index) => (
                                    <option key={`${plan.id}-${index}`} value={plan.id}>
                                        {plan.title}
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">
                                {error}
                            </Form.Control.Feedback>
                        </Col>

                        <Col md="auto">
                            <Button onClick={handleDrugLookup}>
                                Drug Lookup
                            </Button>
                        </Col>
                    </Row>
                )}

                {selectedPlan && (selectedLetter || selectedDrug) && (
                    <>
                        <div className="mt-4">
                            <Typeahead
                                id="drug-search-typeahead"
                                labelKey={(option: any) => option?.code?.text || ''}
                                options={
                                    isTextSearch
                                        ? suggestionData?.data || []
                                        : []
                                }
                                minLength={3}
                                onInputChange={(text) => {
                                    setSearchTerm(text)
                                    setSelectedDrug('')

                                    if (text.length === 0) {
                                        setSelectedLetter('A')
                                    }
                                }}
                                onChange={(selected: any[]) => {
                                    if (!selected.length) return

                                    const selectedItem = selected[0]
                                    const name = selectedItem?.code?.text || ''

                                    setSelectedDrug(name)
                                    setSearchTerm(name)

                                    const firstLetter = name.charAt(0).toUpperCase()
                                    setSelectedLetter(firstLetter)
                                }}
                                isLoading={isFetchingSuggestions}
                                placeholder="Search for a drug..."
                                clearButton
                                renderMenuItemChildren={(option: any) => (
                                    <div>{option?.code?.text}</div>
                                )}
                            />
                        </div>

                        {!selectedDrug && (
                            <div className="mt-4">
                                <h6>Index by drug name</h6>
                                <div className="d-flex gap-2">
                                    {alphabet.map((letter: string) => (
                                        <Button
                                            key={letter}
                                            size="sm"
                                            onClick={() => {
                                                if (letter === selectedLetter) return
                                                setSelectedLetter(letter)
                                                setSelectedDrug('')
                                                setSearchTerm('')
                                            }}
                                            className={
                                                selectedLetter === letter
                                                    ? 'btn-primary'
                                                    : 'btn-primary-outline'
                                            }
                                        >
                                            {letter}
                                        </Button>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className="mt-4">
                            <DrugResults
                                data={drugLookupData?.data}
                                total={
                                    drugLookupData?.total ||
                                    drugLookupData?.data?.length
                                }
                                isLoading={isFetchingDrugLookup}
                                selectedLetter={selectedLetter}
                            />
                        </div>
                    </>
                )}
            </Container>
        </section>
    )
}
export default DrugLookup